import {compact} from '@benchling/js-utils';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {LookupStepType} from 'lookup-config/LookupConfigModalConstants';
import {
  ProcessFlowchartSchemaFieldMapperConfigSpec,
  ProcessFlowchartSchemaMapperConfigSpec,
} from 'processes/dao/ProcessSchemaDAO';
import {DefaultTaskSchemaNodeAdapter} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/DefaultTaskSchemaNodeAdapter';
import {TaskSchemaStaticNodeData} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/TaskSchemaNodeRenderer';
import {Schema} from 'schemas/SchemaConstants';
import {defaultNodeHandleIdGenerator} from 'ui-library/reactflow/nodes/SimpleBenchlingNode/sub-components/SimpleBenchlingNodeHandle';
import APIUtil from 'util/APIUtil';

import {SchemaInfo} from '../FieldMappingConstants';
import {generateTaskSchemaNodeIdentifier, makeDataFlowEdgeHandle} from './flowchartIdentifierUtils';
import {
  FLOWCHART_TASK_SECONDARY_INFO,
  FLOWCHART_OUTPUT_SECONDARY_INFO,
  ProcessFlowchartNodeType,
  NodeTransitionPosition,
  ProcessFlowchartEdgeType,
  TaskSchemaNodeSection,
  FlowchartStaticEdgeData,
} from './TransitionModalWithFlowchartEditorConstants';

function isFlowchartTaskSchema(schemaInfo: SchemaInfo): boolean {
  return schemaInfo.taskSchema.executionType === ProcessExecutionType.FLOWCHART;
}

/**
 * Generate data for node, using task schema node adapter to customize
 */
export function createStaticNodeData(
  schemaInfo: SchemaInfo,
  nodeTransitionPosition: NodeTransitionPosition
): TaskSchemaStaticNodeData {
  const isFlowchartTask = isFlowchartTaskSchema(schemaInfo);
  const isFlowchartOutputNode = isFlowchartTask && nodeTransitionPosition === NodeTransitionPosition.TARGET;
  // TODO: Use different adapters based on task schema type
  const adapter = new DefaultTaskSchemaNodeAdapter({isFlowchartOutput: isFlowchartOutputNode});
  return {
    id: generateTaskSchemaNodeIdentifier(schemaInfo),
    nodeType: ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
    nodeName: schemaInfo.taskSchema.name,
    /** Show secondary information to differentiate Flowchart Task/Output nodes from task schema nodes. */
    nodeSecondaryInfo: isFlowchartTask
      ? isFlowchartOutputNode
        ? FLOWCHART_OUTPUT_SECONDARY_INFO
        : FLOWCHART_TASK_SECONDARY_INFO
      : undefined,
    sections: adapter.createSections({schemaInfo}),
    hideSectionSummaries: true,
  };
}

export function buildControlFlowEdge({
  sourceNodeId,
  targetNodeId,
}: {
  sourceNodeId: string;
  targetNodeId: string;
}): FlowchartStaticEdgeData {
  return {
    id: uuidv4(),
    source: sourceNodeId,
    target: targetNodeId,
    sourceHandle: defaultNodeHandleIdGenerator(sourceNodeId, 'source'),
    targetHandle: defaultNodeHandleIdGenerator(targetNodeId, 'target'),
    type: ProcessFlowchartEdgeType.CONTROL_FLOW,
  };
}

export function buildDataFlowEdge({
  sourceNodeId,
  targetNodeId,
  fromFieldId,
  fromFieldSection,
  toFieldId,
  toFieldSection,
}: {
  sourceNodeId: string;
  targetNodeId: string;
  fromFieldId: string;
  fromFieldSection: TaskSchemaNodeSection;
  toFieldId: string;
  toFieldSection: TaskSchemaNodeSection;
}): FlowchartStaticEdgeData {
  return {
    id: uuidv4(),
    source: sourceNodeId,
    target: targetNodeId,
    sourceHandle: makeDataFlowEdgeHandle(fromFieldId, fromFieldSection, 'source'),
    targetHandle: makeDataFlowEdgeHandle(toFieldId, toFieldSection, 'target'),
    type: ProcessFlowchartEdgeType.DATA_FLOW,
  };
}

interface TransitionData {
  sourceNodesStaticData: Array<TaskSchemaStaticNodeData>;
  targetNodeStaticData: TaskSchemaStaticNodeData;
  edgesData: Array<FlowchartStaticEdgeData>;
}

interface BuildTransitionDataFromTaskDefinitionsRunnerArgs {
  fromSchemaInfos: Array<SchemaInfo>;
  toSchemaInfo: SchemaInfo;
  schemaMapperConfigSpec: ProcessFlowchartSchemaMapperConfigSpec | null;
}

class BuildTransitionDataFromTaskDefinitionsRunner {
  private readonly fromSchemaInfos: Array<SchemaInfo>;
  private readonly toSchemaInfo: SchemaInfo;
  private readonly fromTaskSchemaIdToNodeIdMap: Map<string, string>;
  private readonly schemaMapperConfigSpec: ProcessFlowchartSchemaMapperConfigSpec | null;
  private sourceNodesStaticData: Array<TaskSchemaStaticNodeData>;
  private targetNodeStaticData: TaskSchemaStaticNodeData | null;
  private edgesData: Array<FlowchartStaticEdgeData>;

  constructor(args: BuildTransitionDataFromTaskDefinitionsRunnerArgs) {
    this.fromSchemaInfos = args.fromSchemaInfos;
    this.toSchemaInfo = args.toSchemaInfo;
    this.schemaMapperConfigSpec = args.schemaMapperConfigSpec;
    this.fromTaskSchemaIdToNodeIdMap = new Map<string, string>();
    this.sourceNodesStaticData = [];
    this.targetNodeStaticData = null;
    this.edgesData = [];
  }

  run(): TransitionData {
    this.convertSchemaInfoToNodes();
    this.convertTransitionInfoToEdges();
    return {
      sourceNodesStaticData: this.sourceNodesStaticData,
      targetNodeStaticData: this.targetNodeStaticData!,
      edgesData: this.edgesData,
    };
  }

  private convertSchemaInfoToNodes(): void {
    this.sourceNodesStaticData = this.fromSchemaInfos.map((schemaInfo) => {
      const node = createStaticNodeData(schemaInfo, NodeTransitionPosition.SOURCE);
      this.fromTaskSchemaIdToNodeIdMap.set(schemaInfo.taskSchema.id, node.id);
      return node;
    });

    this.targetNodeStaticData = createStaticNodeData(this.toSchemaInfo, NodeTransitionPosition.TARGET);
  }

  private convertTransitionInfoToEdges(): void {
    this.edgesData = [...this.buildDataFlowEdges(), ...this.buildControlFlowEdges()];
  }

  private buildControlFlowEdges(): Array<FlowchartStaticEdgeData> {
    const targetNodeId = this.targetNodeStaticData!.id;
    return this.sourceNodesStaticData.map((sourceStaticData) =>
      buildControlFlowEdge({sourceNodeId: sourceStaticData.id, targetNodeId})
    );
  }

  private buildDataFlowEdges(): Array<FlowchartStaticEdgeData> {
    if (this.schemaMapperConfigSpec == null) {
      return [];
    }
    const toFieldSection = isFlowchartTaskSchema(this.toSchemaInfo)
      ? TaskSchemaNodeSection.OUTPUTS
      : TaskSchemaNodeSection.INPUTS;
    return compact(
      this.schemaMapperConfigSpec.schemaFieldMapperConfigs.map((fieldMapperConfig) => {
        const fromFieldInfo = this.getUpstreamMappingInfoFromFieldMapperConfig(fieldMapperConfig);
        if (fromFieldInfo == null) {
          return null;
        }
        const {fromTaskSchemaId, fromFieldId, fromFieldSection} = fromFieldInfo;
        const toFieldId = fieldMapperConfig.toSchemaFieldId;
        const sourceNodeId = this.fromTaskSchemaIdToNodeIdMap.get(fromTaskSchemaId)!;
        const targetNodeId = this.targetNodeStaticData!.id;
        assert(sourceNodeId != null, 'Source node not found');
        assert(targetNodeId != null, 'Target node not found');
        return buildDataFlowEdge({
          sourceNodeId,
          targetNodeId,
          fromFieldId,
          fromFieldSection,
          toFieldId,
          toFieldSection,
        });
      })
    );
  }

  private getUpstreamMappingInfoFromFieldMapperConfig(
    fieldMapperConfig: ProcessFlowchartSchemaFieldMapperConfigSpec
  ): {
    fromTaskSchemaId: string;
    fromFieldSection: TaskSchemaNodeSection;
    fromFieldId: string;
  } | null {
    const fromSchemaId = fieldMapperConfig.fromSchemaId;
    const fromTaskSchema = this.fromSchemaInfos.find(
      (fromSchemaInfo) =>
        fromSchemaInfo.taskSchema.id === fromSchemaId ||
        fromSchemaInfo.taskSchema.outputSchema?.id === fromSchemaId
    )!.taskSchema;

    if (fromTaskSchema == null) {
      return null;
    }
    // The first lookup step that is a schema field lookup is expected to be associated with the task schema node
    // (either the "output" of the node, "input" of the node, or the "root" of the flowchart (which we include
    // in the node))
    const fromFieldIdOrName = fieldMapperConfig.lookupConfig.lookupSteps.find(
      (lookupStep) => lookupStep.type === LookupStepType.SCHEMA_FIELD
    )?.schemaField;
    if (fromFieldIdOrName == null) {
      return null;
    } else if (!APIUtil.isSchemaField(fromFieldIdOrName)) {
      return null;
    }

    const isFromFieldInSchema = (schema: Schema): boolean =>
      schema.allFields.some((field) => field.apiId === fromFieldIdOrName);

    const throwCouldNotDetermineSectionError = (): never => {
      assert(false, `Could not determine the section of the source field`, {
        fieldMapperConfig,
      });
    };

    // TODO(BNCH-123623): Handle the case where the source field is from the root
    let fromFieldSection!: TaskSchemaNodeSection;
    if (APIUtil.isProcessOutputSchema(fromSchemaId)) {
      if (fieldMapperConfig.lookupConfig.lookupSteps[0].type === LookupStepType.PROCESS_TASK) {
        assert(
          isFromFieldInSchema(fromTaskSchema),
          'Expected the source field to be a valid field in the task schema.'
        );
        fromFieldSection = TaskSchemaNodeSection.INPUTS;
      } else if (fromTaskSchema.outputSchema != null && isFromFieldInSchema(fromTaskSchema.outputSchema)) {
        fromFieldSection = TaskSchemaNodeSection.OUTPUTS;
      } else {
        throwCouldNotDetermineSectionError();
      }
    } else if (isFromFieldInSchema(fromTaskSchema)) {
      fromFieldSection = TaskSchemaNodeSection.INPUTS;
    } else {
      throwCouldNotDetermineSectionError();
    }

    return {fromTaskSchemaId: fromTaskSchema.id, fromFieldSection, fromFieldId: fromFieldIdOrName};
  }
}

export default function buildTransitionDataFromTaskDefinitions(
  args: BuildTransitionDataFromTaskDefinitionsRunnerArgs
): TransitionData {
  const runner = new BuildTransitionDataFromTaskDefinitionsRunner(args);
  return runner.run();
}
